import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Img,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import moment from "moment-timezone";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactPaginate from "react-paginate";
import { clientAPI } from "../../../api/client";
import useDebounce from "../../../hooks/useDebounce";
import SearchableSelect from "../../../components/selectForm";
import calendarIcon from "../../../assets/images/onleave/calendar.svg";
import chevronLeft from "../../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../../assets/images/employee/chevron-right.svg";
import iconApproved from "../../../assets/images/receiveSalary/check-circle.svg";
import iconRejected from "../../../assets/images/contract/x-circle.svg";

/**
 * Chấm công luồng mới (Bước 5): Quản lý chấm công -> tab "Đơn chỉnh sửa công".
 * Giao diện theo tab "Danh sách chấm công", nút Duyệt / Ko duyệt như Quản lý booking.
 * Duyệt 2 bước (26/10/2026): Quản lý trực tiếp duyệt bước 1 trên app (chỉ ghi nhận), HCNS duyệt bước 2
 * ở đây mới ghi nhận vào công. HCNS duyệt / từ chối được bất kể bước 1; lọc "Quản lý duyệt" để biết đơn nào
 * quản lý đã duyệt.
 */

const TZ = "Asia/Ho_Chi_Minh";
const baseURL = process.env.REACT_APP_BACKEND_API || "http://localhost:3001";
const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { key: "PENDING", value: "Chờ duyệt" },
  { key: "APPROVED", value: "Đã duyệt" },
  { key: "REJECTED", value: "Từ chối" },
];

// Bước 1 - Quản lý trực tiếp (app)
const LEADER_STATUS_OPTIONS = [
  { key: "PENDING", value: "Chưa duyệt" },
  { key: "APPROVED", value: "Đã duyệt" },
  { key: "REJECTED", value: "Từ chối" },
];

const HEADERS = [
  { label: "HCNS duyệt (bước 2)", key: "status" },
  { label: "Quản lý duyệt (bước 1)", key: "leader" },
  { label: "Ngày công", key: "date" },
  { label: "Mã nhân viên", key: "code" },
  { label: "Tên nhân viên", key: "fullName" },
  { label: "Ca", key: "session" },
  { label: "Giờ hiện tại", key: "oldTime" },
  { label: "Giờ đề nghị", key: "newTime" },
  { label: "Lý do", key: "reason" },
  { label: "Ảnh", key: "images" },
  { label: "Người xử lý", key: "handler" },
];

const fmtDate = (d) => (d ? moment(d).tz(TZ).format("DD/MM/YYYY") : "-");
const fmtTime = (d) => (d ? moment(d).tz(TZ).format("HH:mm") : "--:--");
const toYmd = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");
// Đơn không có giờ ra đề nghị => giữ giờ ra hiện tại của lượt chấm
const fmtNewOut = (d) => (d ? fmtTime(d) : "giữ giờ ra hiện tại");

const errorMessage = (error) => {
  const m = error?.response?.data?.message;
  return Array.isArray(m) ? m.join(", ") : m || "Có lỗi xảy ra";
};

/** Trạng thái giống Quản lý booking: chờ duyệt -> nút Duyệt / Ko duyệt */
const StatusCell = ({ status, onApprove, onReject }) => {
  if (status === "APPROVED") {
    return (
      <Flex w={"max-content"} alignItems={"center"} gap={1}>
        <Image src={iconApproved} w={"20px"} />
        <Text color={"var(--color-status-employ)"}>Đã được duyệt</Text>
      </Flex>
    );
  }
  if (status === "REJECTED") {
    return (
      <Flex w={"max-content"} alignItems={"center"} gap={1}>
        <Image src={iconRejected} w={"20px"} />
        <Text color={"var(--text-red-employee)"}>Không được phê duyệt</Text>
      </Flex>
    );
  }
  return (
    <Flex gap={"8px"} w={"max-content"}>
      <Button
        bg={"var(--linear-gradient-employee)"}
        color={"var(--color-main)"}
        fontSize={"12px"}
        lineHeight={"17px"}
        fontWeight={"600"}
        _hover={{ filter: "brightness(90%)" }}
        onClick={onApprove}
        h={"28px"}
        minH={"28px"}
        w={"90px"}
      >
        Duyệt
      </Button>
      <Button
        bg={"var(--color-main)"}
        color={"var(--color-option-employee-hover)"}
        border={"1px solid var(--color-option-employee-hover)"}
        fontSize={"12px"}
        lineHeight={"17px"}
        fontWeight={"600"}
        _hover={{ filter: "brightness(90%)" }}
        onClick={onReject}
        h={"28px"}
        minH={"28px"}
        w={"90px"}
      >
        Ko duyệt
      </Button>
    </Flex>
  );
};

/** Kết quả bước 1 (quản lý trực tiếp duyệt trên app) */
const LeaderCell = ({ item }) => {
  // Đơn cũ (trước khi duyệt 2 bước) quản lý duyệt trên app là xong luôn => không có bước 1 riêng
  if (item.status !== "PENDING" && item.handledFrom === "APP" && !item.leaderHandledAt) {
    return "-";
  }
  const by = item.leaderHandler?.fullName
    ? `${item.leaderHandler.fullName} (${fmtDate(item.leaderHandledAt)} ${fmtTime(
        item.leaderHandledAt,
      )})`
    : null;
  if (item.leaderStatus === "APPROVED" || item.leaderStatus === "REJECTED") {
    const approved = item.leaderStatus === "APPROVED";
    return (
      <Flex flexDirection={"column"} gap={1}>
        <Flex w={"max-content"} alignItems={"center"} gap={1}>
          <Image src={approved ? iconApproved : iconRejected} w={"20px"} />
          <Text
            color={approved ? "var(--color-status-employ)" : "var(--text-red-employee)"}
          >
            {approved ? "Đã được duyệt" : "Không được phê duyệt"}
          </Text>
        </Flex>
        {by ? <Text>{by}</Text> : null}
        {!approved && item.leaderRejectReason ? (
          <Text whiteSpace={"normal"}>Lý do: {item.leaderRejectReason}</Text>
        ) : null}
      </Flex>
    );
  }
  return <Text color={"var(--text-color-Subtittle)"}>Chưa duyệt</Text>;
};

export default function CorrectionRequests() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(null);
  const [leaderStatus, setLeaderStatus] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [currentPage, setCurrentPage] = useState(1);
  const [handling, setHandling] = useState(null); // { item, status }
  const [rejectReason, setRejectReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const debouncedSearch = useDebounce(search, 500);

  const onChangeSearch = useCallback((e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  }, []);

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: [
      "checkinCorrection",
      currentPage,
      debouncedSearch,
      status,
      leaderStatus,
      toYmd(startDate),
      toYmd(endDate),
    ],
    queryFn: async () => {
      const options = { page: currentPage, limit: PAGE_SIZE };
      if (debouncedSearch) options.search = debouncedSearch;
      if (status) options.status = status;
      if (leaderStatus) options.leaderStatus = leaderStatus;
      if (startDate) options.fromDate = toYmd(startDate);
      if (endDate) options.toDate = toYmd(endDate);
      const qs = new URLSearchParams(options).toString();
      return clientAPI("get", `/checkinCorrection?${qs}`);
    },
    refetchOnWindowFocus: false,
  });

  const items = useMemo(() => data?.items || [], [data]);
  const entries = data?.pagination?.total || 0;
  const totalPages = Math.ceil(entries / PAGE_SIZE);
  const startEntry = entries ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const endEntry = Math.min(currentPage * PAGE_SIZE, entries);

  const closeHandle = () => {
    setHandling(null);
    setRejectReason("");
  };

  const submitHandle = async () => {
    if (!handling) return;
    setSubmitting(true);
    try {
      await clientAPI("put", `/checkinCorrection/${handling.item._id}/handle`, {
        status: handling.status,
        ...(handling.status === "REJECTED" && rejectReason.trim()
          ? { rejectReason: rejectReason.trim() }
          : {}),
      });
      toast.success(
        handling.status === "APPROVED" ? "Đã duyệt đơn" : "Đã từ chối đơn",
      );
      closeHandle();
      refetch();
    } catch (error) {
      toast.error(errorMessage(error));
    }
    setSubmitting(false);
  };

  const cellValue = (item, key) => {
    switch (key) {
      case "date":
        return fmtDate(item.date);
      case "code":
        return item.employeeObject?.code || "-";
      case "fullName":
        return item.employeeObject?.fullName || "-";
      case "session":
        return item.sessionName || "-";
      case "oldTime":
        return `${fmtTime(item.oldCheckinTime)} - ${fmtTime(item.oldCheckoutTime)}`;
      case "newTime":
        return `${fmtTime(item.checkinTime)} - ${fmtNewOut(item.checkoutTime)}`;
      case "reason":
        return item.rejectReason
          ? `${item.reason} (Từ chối: ${item.rejectReason})`
          : item.reason || "-";
      case "images":
        return item.images?.length ? (
          <Flex gap="8px" flexWrap="wrap" maxW="140px">
            {item.images.map((img, i) => (
              <Link key={img} href={`${baseURL}/${img}`} isExternal color="blue.500">
                Ảnh {i + 1}
              </Link>
            ))}
          </Flex>
        ) : (
          "-"
        );
      case "status":
        return (
          <StatusCell
            status={item.status}
            onApprove={() => setHandling({ item, status: "APPROVED" })}
            onReject={() => setHandling({ item, status: "REJECTED" })}
          />
        );
      case "leader":
        return <LeaderCell item={item} />;
      case "handler":
        return item.handler?.fullName
          ? `${item.handler.fullName} (${item.handledFrom === "APP" ? "App" : "Web"} ${fmtDate(
              item.handledAt,
            )} ${fmtTime(item.handledAt)})`
          : "-";
      default:
        return "";
    }
  };

  return (
    <Flex flexDirection={"column"} gap={{ base: "16px" }}>
      <Flex
        minHeight="calc(100vh - 167px)"
        backgroundColor="var(--color-main)"
        borderRadius={{ base: "12px" }}
        flexDirection={"column"}
      >
        <Flex
          alignItems={"flex-start"}
          gap={{ base: "16px 32px" }}
          flexDirection={"column"}
          p={"16px 0px 12px 16px"}
        >
          <InputGroup
            maxW="42vw"
            minW={"42vw"}
            borderRadius={{ base: "8px" }}
            w={{ base: "100%" }}
          >
            <Input
              h={"44px"}
              placeholder="Nhập MVN, họ tên, lý do..."
              type="text"
              borderRadius={{ base: "8px" }}
              border={{ base: "1px solid var(--color-secondary)" }}
              onChange={onChangeSearch}
            />
            <InputRightElement
              h={"44px"}
              borderTopRightRadius={"8px"}
              borderBottomRightRadius={"8px"}
              pointerEvents="none"
              backgroundImage="var(--color-button)"
            >
              <SearchIcon color="var(--color-main)" />
            </InputRightElement>
          </InputGroup>

          <Flex
            alignItems={"center"}
            gap={{ base: "16px 32px" }}
            flexWrap={"wrap"}
            w={"100%"}
          >
            <InputGroup maxW={"255px"} zIndex={22}>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                locale="vi"
                onChange={(update) => {
                  setDateRange(update);
                  setCurrentPage(1);
                }}
                isClearable={true}
                placeholderText="Ngày công từ - đến"
                className="datePickerStyle"
              />
              {!startDate && !endDate && (
                <InputRightElement pointerEvents="none" h={"44px"}>
                  <Image src={calendarIcon} />
                </InputRightElement>
              )}
            </InputGroup>
            <SearchableSelect
              options={STATUS_OPTIONS}
              initialSelectedOption={{ key: 0, value: "HCNS duyệt: tất cả" }}
              setValue={(v) => {
                setStatus(v || null);
                setCurrentPage(1);
              }}
            />
            <SearchableSelect
              options={LEADER_STATUS_OPTIONS}
              initialSelectedOption={{ key: 0, value: "Quản lý duyệt: tất cả" }}
              setValue={(v) => {
                setLeaderStatus(v || null);
                setCurrentPage(1);
              }}
            />
          </Flex>
        </Flex>

        <Flex
          flexDirection={"column"}
          justifyContent={"space-between"}
          width={"100%"}
          minH="calc(100vh - 235px)"
        >
          <Skeleton isLoaded={!isLoading && !isRefetching}>
            <TableContainer
              w={"100%"}
              pb={"32px"}
              maxH="calc(100vh - 350.5px)"
              overflowY="auto"
            >
              <Table w={"100%"} variant="unstyled">
                <Thead
                  position="sticky"
                  top="0"
                  zIndex="1"
                  w={"100%"}
                  h={"41px"}
                  backgroundColor="#F9FAFD"
                >
                  <Tr h={{ base: "41px" }}>
                    {HEADERS.map((h, index) => (
                      <Td
                        p={"16px 16px 8px 16px"}
                        key={index}
                        border={"none"}
                        color={"#51607B"}
                        fontSize={{ base: "14px" }}
                      >
                        <Text fontWeight={"bold"} textAlign={"start"}>
                          {h.label}
                        </Text>
                      </Td>
                    ))}
                  </Tr>
                </Thead>
                <Tbody w={"100%"} h={"100%"}>
                  {items.length > 0 ? (
                    items.map((item, rowIndex) => {
                      const bg =
                        rowIndex % 2 === 0
                          ? "var(--color-main)"
                          : "var(--fill-avatar)";
                      return (
                        <Tr key={item._id} h={"41px"}>
                          {HEADERS.map((h) => (
                            <Td
                              key={h.key}
                              p={"16px"}
                              bg={bg}
                              maxW={
                                h.key === "reason" || h.key === "leader" ? "320px" : undefined
                              }
                            >
                              <Box
                                fontSize={{ base: "14px" }}
                                lineHeight={"19.6px"}
                                textAlign={"start"}
                                fontWeight={h.key === "newTime" ? 600 : 500}
                                whiteSpace={h.key === "reason" ? "normal" : "nowrap"}
                                color="var(--color-option-employee)"
                              >
                                {cellValue(item, h.key)}
                              </Box>
                            </Td>
                          ))}
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td
                        colSpan={HEADERS.length}
                        textAlign="center"
                        padding={"40px 0"}
                      >
                        Không có dữ liệu
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </Skeleton>

          <Flex
            m={"50px 16px 16px 16px"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Text
              fontSize={"14px"}
              fontWeight={500}
              lineHeight={"20px"}
              color={"var(--text-color-Subtittle)"}
            >
              Showing {startEntry} to {endEntry} of {entries} entries
            </Text>
            <HStack spacing={2} justify="flex-end">
              <ReactPaginate
                className="pagination-custom"
                pageCount={totalPages}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                onPageChange={(p) => setCurrentPage(p.selected + 1)}
                containerClassName={"pagination"}
                activeClassName={"active"}
                breakClassName={"ellipsis"}
                breakLabel={"..."}
                previousLabel={
                  <Img w={"16px"} minW={"16px"} h={"16px"} src={chevronLeft} />
                }
                nextLabel={
                  <Img w={"16px"} minW={"16px"} h={"16px"} src={chevronRight} />
                }
                renderOnZeroPageCount={null}
                forcePage={currentPage - 1}
              />
            </HStack>
          </Flex>
        </Flex>
      </Flex>

      <Modal isOpen={!!handling} onClose={closeHandle} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius={"16px"}>
          <ModalHeader>
            {handling?.status === "APPROVED"
              ? "Duyệt đơn chỉnh sửa công"
              : "Từ chối đơn chỉnh sửa công"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="8px" fontSize="14px">
              {handling?.item?.employeeObject?.fullName} - ngày{" "}
              {fmtDate(handling?.item?.date)}: vào{" "}
              {fmtTime(handling?.item?.checkinTime)}, ra{" "}
              {fmtNewOut(handling?.item?.checkoutTime)}
            </Text>
            {handling?.item &&
            handling.item.leaderStatus !== "APPROVED" ? (
              <Text mb="8px" fontSize="14px" color="var(--text-red-employee)">
                {handling.item.leaderStatus === "REJECTED"
                  ? "Quản lý đã từ chối đơn này ở bước 1."
                  : "Quản lý chưa duyệt đơn này ở bước 1."}
              </Text>
            ) : null}
            {handling?.status === "APPROVED" ? (
              <Text fontSize="14px" color="var(--text-color-Subtittle)">
                Bản ghi chấm công sẽ được cập nhật theo giờ trên, công tính lại
                theo luật đi muộn, lý do ghi "[Đơn chỉnh sửa] + lý do".
              </Text>
            ) : (
              <Textarea
                placeholder="Lý do từ chối (không bắt buộc)"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
            )}
          </ModalBody>
          <ModalFooter gap="12px">
            <Button
              h="44px"
              variant="outline"
              border="1px solid var(--color-secondary)"
              onClick={closeHandle}
            >
              Huỷ
            </Button>
            <Button
              h="44px"
              color="var(--color-main)"
              background={
                handling?.status === "APPROVED"
                  ? "var(--linear-gradient-employee)"
                  : "var(--text-red-employee)"
              }
              _hover={{ filter: "brightness(90%)" }}
              isLoading={submitting}
              onClick={submitHandle}
            >
              {handling?.status === "APPROVED" ? "Duyệt" : "Từ chối"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
